import { AsyncPipe, NgClass } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    forwardRef,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges
} from "@angular/core";
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";
import { NgMultiLabelTemplateDirective, NgOptionTemplateDirective, NgSelectComponent } from "@ng-select/ng-select";
import { isObservable, Observable, of } from "rxjs";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { RequiredDirective } from "../../directives/required/required.directive";
import { ValidationMessageComponent } from "../../validation/validation-message/validation-message.component";
import { ControlValueAccessorBaseComponent } from "../base-component/control-value-accessor-base.component";
import { IconButtonComponent } from "../icon-button/icon-button.component";
import { InfoComponent } from "../info/info.component";

export type IntentType = "save" | "delete" | "edit";

@Component({
    providers: [
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SimpleSelectorComponent)
        },
        {
            multi: false,
            provide: ControlValueAccessorBaseComponent,
            useExisting: forwardRef(() => SimpleSelectorComponent)
        }
    ],
    selector: "rask-simple-selector",
    templateUrl: "./simple-selector.component.html",
    styleUrls: ["./simple-selector.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        RequiredDirective,
        InfoComponent,
        NgSelectComponent,
        FormsModule,
        NgMultiLabelTemplateDirective,
        NgOptionTemplateDirective,
        NgClass,
        IconButtonComponent,
        ValidationMessageComponent,
        AsyncPipe
    ]
})
export class SimpleSelectorComponent extends ControlValueAccessorBaseComponent implements OnInit, ControlValueAccessor, OnChanges {
    @Input() values: any[] | Observable<any[]>;
    @Input() bindLabel?: string;
    @Input() bindValue?: string;
    @Input() portalToBody = null;
    @Input() clearable = false;
    @Input() intent: IntentType;
    @Input() onBeforeChange: ($event: any) => Observable<boolean>;
    @Input() placeholder: string;

    @Output() itemIconClick? = new EventEmitter<any>();

    items$: Observable<any[]>;
    selected: any;
    isPopupOpen = false;

    readonly targetId = `rask-simple-selector-target-${GuidUtils.newGuid()}`;
    private isInited = false;

    constructor() {
        super();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.isInited) {
            return;
        }

        if (isObservable(this.values)) {
            this.items$ = this.values;
        } else {
            this.items$ = of(this.values);
        }
    }

    @Input() viewFormatter = (item: any) => this.bindLabel ? item[this.bindLabel] : item;
    @Input() viewSelectedFormatter = (item: any) => this.bindLabel ? item[this.bindLabel] : item;
    @Input() itemDisabled = (item: any) => false;

    ngOnInit(): void {
        super.ngOnInit();

        this.selected = this.value;
        if (isObservable(this.values)) {
            this.items$ = this.values;
        } else {
            this.items$ = of(this.values);
        }
        this.isInited = true;
    }

    onSelectionChange(item: any) {
        if (this.itemDisabled(item)) {
            return;
        }

        let worker: Observable<any> = of(true);
        if (this.onBeforeChange) {
            worker = this.onBeforeChange(item);
        }

        worker.subscribe({
            next: (result) => {
                if (result) {
                    this.value = this.selected;
                } else {
                    this.selected = this.value;
                }
            },
            error: () => this.selected = this.value
        });
    }

    onOpen($event) {
        this.isPopupOpen = true;
        this.focus.next(true);
    }

    onClose($event) {
        this.isPopupOpen = false;
        this.focus.next(false);
    }

    writeValue(value: any): void {
        this.selected = value;
    }

    clearSelection(): void {
        this.writeValue(null);
    }

    get iconClass(): string {
        switch (this.intent) {
            case "save":
                return "far fa-save warning-orange";
            case "delete":
                return "far fa-trash-alt warning-red";
            case "edit":
                return "far fa-pencil warning-orange";
            default:
                return "";
        }
    }

    protected onIconClick($event: any, item: any): void {
        $event.stopPropagation();
        $event.preventDefault();
        this.itemIconClick.emit(item);
    }
}
